import { QueryClient, QueryFunction } from "@tanstack/react-query";
import type { Business, Service } from "@shared/schema";
import {
  apiPublicPath,
  isPublicBookingApiPath,
  parsePagedList,
  publicFetchCredentials,
  resolveBookingApiUrl,
  type BookingApiBusiness,
} from "./bookingApi";
import {
  mapCustomerResponseToCustomer,
  normalizeAdminServicesListPayload,
  normalizeBookingsListPayload,
  type CustomerDetailResponse,
} from "./adminApi";
import { getAccessToken } from "./authAccessToken";

/**
 * Set by `ApiContextProvider` so admin `useQuery` calls use `apiFetchUsingAuth`
 * (Bearer + 401 → `/refresh` + retry), matching eposnow.myaccount.ui.
 */
let bookingAdminJsonFetch: ((resolvedUrl: string) => Promise<unknown>) | null = null;

export function setBookingAdminQueryJsonFetch(fn: ((resolvedUrl: string) => Promise<unknown>) | null) {
  bookingAdminJsonFetch = fn;
}

type AdminMutationFn = (method: string, resolvedUrl: string, data?: unknown) => Promise<unknown>;

let bookingAdminMutationFn: AdminMutationFn | null = null;

export function setBookingAdminMutationFn(fn: AdminMutationFn | null) {
  bookingAdminMutationFn = fn;
}

function requestPath(url: string): string {
  try {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return new URL(url).pathname;
    }
  } catch {
    /* ignore */
  }
  return url.split("?")[0];
}

function isAdminBookingUrl(url: string): boolean {
  const p = requestPath(url);
  return p.startsWith("/v1/booking/admin") || p.startsWith("/api/admin");
}

/** Fallback when `setBookingAdminQueryJsonFetch` is unset: Bearer from `authAccessToken` (synced from `ApiContext`). */
export function headersForBookingUrl(url: string, jsonBody: boolean): HeadersInit {
  const h = new Headers();
  if (jsonBody) h.set("Content-Type", "application/json");
  if (isAdminBookingUrl(url)) {
    const at = getAccessToken();
    if (at) h.set("Authorization", `Bearer ${at}`);
  }
  return h;
}

export async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/** Matches OpenAPI admin bookings `limit` max (200). */
export const ADMIN_BOOKINGS_MAX_PAGE_SIZE = 200;

/** Default page size for admin list endpoints that return `PagedListResponse` (`metadata` / `_links`). */
export const ADMIN_LIST_PAGE_SIZE = 20;

/** Max `limit` for admin services/staff list (aligned with bookings cap). */
export const ADMIN_LIST_MAX_PAGE_SIZE = 200;

/**
 * GET `/v1/booking/admin/bookings` with `page` / `limit` query params (see OpenAPI).
 * Uses the same auth path as other admin booking queries (`bookingAdminJsonFetch` when set).
 */
export async function fetchAdminBookingListJson(page: number, limit: number): Promise<unknown> {
  const safePage = Math.max(1, Math.floor(page));
  const safeLimit = Math.min(ADMIN_BOOKINGS_MAX_PAGE_SIZE, Math.max(1, Math.floor(limit)));
  const path = `/api/admin/bookings?page=${safePage}&limit=${safeLimit}`;
  const resolved = resolveBookingApiUrl(path);

  if (bookingAdminJsonFetch && isAdminBookingUrl(resolved)) {
    return bookingAdminJsonFetch(resolved);
  }

  const res = await fetch(resolved, {
    credentials: isPublicBookingApiPath(resolved) ? publicFetchCredentials() : "include",
    headers: headersForBookingUrl(resolved, false),
  });
  await throwIfResNotOk(res);
  return res.json();
}

/**
 * GET `/v1/booking/admin/services` with `page` / `limit` (PagedListResponse when API supports it).
 * Optional `activeOnly` matches OpenAPI `activeOnly` query param.
 */
export async function fetchAdminServicesListJson(
  page: number,
  limit: number,
  options?: { activeOnly?: boolean },
): Promise<unknown> {
  const safePage = Math.max(1, Math.floor(page));
  const safeLimit = Math.min(ADMIN_LIST_MAX_PAGE_SIZE, Math.max(1, Math.floor(limit)));
  const params = new URLSearchParams();
  params.set("page", String(safePage));
  params.set("limit", String(safeLimit));
  if (options?.activeOnly) params.set("activeOnly", "true");
  const path = `/api/admin/services?${params.toString()}`;
  const resolved = resolveBookingApiUrl(path);

  if (bookingAdminJsonFetch && isAdminBookingUrl(resolved)) {
    return bookingAdminJsonFetch(resolved);
  }

  const res = await fetch(resolved, {
    credentials: isPublicBookingApiPath(resolved) ? publicFetchCredentials() : "include",
    headers: headersForBookingUrl(resolved, false),
  });
  await throwIfResNotOk(res);
  return res.json();
}

/** Fetches every page of GET `/api/admin/services` (for mutations that must touch all rows in a category). */
export async function fetchAllAdminServicesMerged(): Promise<Service[]> {
  const seen = new Set<string>();
  const out: Service[] = [];
  let page = 1;
  let totalPages = 1;
  /* Sequential pagination — each request needs the prior response's `totalPages`. */
  /* eslint-disable no-await-in-loop -- must fetch page N before knowing if N+1 exists */
  do {
    const raw = await fetchAdminServicesListJson(page, ADMIN_LIST_PAGE_SIZE);
    const { services, metadata } = normalizeAdminServicesListPayload(raw);
    for (const s of services) {
      const id = String(s.id);
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(s);
    }
    totalPages = metadata?.totalPages ?? 1;
    page += 1;
  } while (page <= totalPages);
  /* eslint-enable no-await-in-loop */
  return out;
}

/**
 * GET `/v1/booking/admin/staff` with `page` / `limit` (PagedListResponse when API supports it).
 */
export async function fetchAdminStaffListJson(page: number, limit: number): Promise<unknown> {
  const safePage = Math.max(1, Math.floor(page));
  const safeLimit = Math.min(ADMIN_LIST_MAX_PAGE_SIZE, Math.max(1, Math.floor(limit)));
  const path = `/api/admin/staff?page=${safePage}&limit=${safeLimit}`;
  const resolved = resolveBookingApiUrl(path);

  if (bookingAdminJsonFetch && isAdminBookingUrl(resolved)) {
    return bookingAdminJsonFetch(resolved);
  }

  const res = await fetch(resolved, {
    credentials: isPublicBookingApiPath(resolved) ? publicFetchCredentials() : "include",
    headers: headersForBookingUrl(resolved, false),
  });
  await throwIfResNotOk(res);
  return res.json();
}

/** GET `/v1/booking/admin/staff/{id}/hours` (browser: `/api/admin/staff/{id}/hours`). Returns hour rows only (unwraps `PagedListResponse`). */
export async function fetchAdminJsonStaffHours(staffId: string): Promise<unknown> {
  const path = `/api/admin/staff/${encodeURIComponent(staffId)}/hours`;
  const resolved = resolveBookingApiUrl(path);

  let json: unknown;
  if (bookingAdminJsonFetch && isAdminBookingUrl(resolved)) {
    json = await bookingAdminJsonFetch(resolved);
  } else {
    const res = await fetch(resolved, {
      credentials: isPublicBookingApiPath(resolved) ? publicFetchCredentials() : "include",
      headers: headersForBookingUrl(resolved, false),
    });
    await throwIfResNotOk(res);
    json = await res.json();
  }
  const { items } = parsePagedList(json);
  return items;
}

/**
 * OpenAPI splits customer profile and bookings: GET `.../customers/{id}` + GET `.../customers/{id}/bookings`.
 * Merges into the shape used by admin customer screens (`CustomerDetailResponse`).
 */
export async function fetchAdminCustomerDetailMerged(customerId: string): Promise<CustomerDetailResponse> {
  const custPath = `/api/admin/customers/${encodeURIComponent(customerId)}`;
  const bookPath = `/api/admin/customers/${encodeURIComponent(customerId)}/bookings`;

  async function fetchAdminJson(relativePath: string): Promise<unknown> {
    const resolved = resolveBookingApiUrl(relativePath);
    if (bookingAdminJsonFetch && isAdminBookingUrl(resolved)) {
      return bookingAdminJsonFetch(resolved);
    }
    const res = await fetch(resolved, {
      credentials: isPublicBookingApiPath(resolved) ? publicFetchCredentials() : "include",
      headers: headersForBookingUrl(resolved, false),
    });
    await throwIfResNotOk(res);
    return res.json();
  }

  const [customerRaw, bookingsRaw] = await Promise.all([fetchAdminJson(custPath), fetchAdminJson(bookPath)]);
  const customer = mapCustomerResponseToCustomer(customerRaw);
  const {bookings} = normalizeBookingsListPayload(bookingsRaw);
  return { customer, bookings };
}

/** Multipart upload to `/api/admin/upload` (booking page images). */
export async function adminUploadRequest(formData: FormData): Promise<{ url: string }> {
  const url = resolveBookingApiUrl("/api/admin/upload");
  const headers = new Headers();
  const at = getAccessToken();
  if (at) headers.set("Authorization", `Bearer ${at}`);
  const res = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
    credentials: "include",
  });
  await throwIfResNotOk(res);
  return res.json();
}

function stringifyMutationResult(result: unknown): string {
  if (result === undefined || result === null) return "";
  if (typeof result === "string") return result;
  return JSON.stringify(result);
}

function headersForPublicBooking(jsonBody: boolean): HeadersInit {
  const h = new Headers();
  if (jsonBody) h.set("Content-Type", "application/json");
  return h;
}

/**
 * Anonymous public booking API (no admin Bearer). Uses `publicFetchCredentials()` (omit or include per env).
 * All browser HTTP for the app should go through this, `apiRequest`, `accountPortalRequest`, or `ApiContext.apiFetch`.
 */
export async function publicBookingRequest(
  method: string,
  path: string,
  data?: unknown,
): Promise<Response> {
  const resolved = resolveBookingApiUrl(path);
  const m = method.toUpperCase();
  const hasBody = data !== undefined && m !== "GET" && m !== "HEAD";
  const res = await fetch(resolved, {
    method: m,
    headers: headersForPublicBooking(hasBody),
    body: hasBody ? JSON.stringify(data) : undefined,
    credentials: publicFetchCredentials(),
  });
  await throwIfResNotOk(res);
  return res;
}

/** Public business config for booking page / account login (same as former `bookingApi.fetchPublicBookingConfig`). */
export async function fetchPublicBookingConfig(
  slug: string,
  opts?: { preview?: boolean },
): Promise<BookingApiBusiness> {
  const base = apiPublicPath(slug, "/config");
  const path = opts?.preview ? `${base}?preview=1` : base;
  const res = await publicBookingRequest("GET", path);
  return res.json() as Promise<BookingApiBusiness>;
}

/**
 * Customer portal routes (`/api/account/*`, `/api/manage/*`) use the magic-link JWT, not admin `ApiContext` Bearer.
 */
export async function accountPortalRequest(
  method: string,
  path: string,
  bearerToken: string,
  data?: unknown,
): Promise<Response> {
  const resolved = resolveBookingApiUrl(path);
  const m = method.toUpperCase();
  const hasBody = data !== undefined && m !== "GET" && m !== "HEAD";
  const headers = new Headers();
  headers.set("Authorization", `Bearer ${bearerToken}`);
  if (hasBody) headers.set("Content-Type", "application/json");
  const res = await fetch(resolved, {
    method: m,
    headers,
    body: hasBody ? JSON.stringify(data) : undefined,
    credentials: "include",
  });
  await throwIfResNotOk(res);
  return res;
}

/**
 * Admin GET that treats 404 as empty (e.g. blockouts not deployed). Uses `bookingAdminMutationFn` when set.
 */
export async function adminBookingGetAllow404(path: string): Promise<Response | null> {
  const resolved = resolveBookingApiUrl(path);
  if (bookingAdminMutationFn && isAdminBookingUrl(resolved)) {
    try {
      const result = await bookingAdminMutationFn("GET", resolved, undefined);
      if (result instanceof Response) {
        if (result.status === 404) return null;
        await throwIfResNotOk(result);
        return result;
      }
      const body = stringifyMutationResult(result);
      return new Response(body, {
        status: 200,
        headers: { "Content-Type": "application/json" },
      });
    } catch (e) {
      const err = e as { code?: number };
      if (err?.code === 404) return null;
      throw e;
    }
  }

  const res = await fetch(resolved, {
    method: "GET",
    headers: headersForBookingUrl(resolved, false),
    credentials: isPublicBookingApiPath(resolved) ? publicFetchCredentials() : "include",
  });
  if (res.status === 404) return null;
  await throwIfResNotOk(res);
  return res;
}

/** React Query key for GET `/api/admin/business` (use with `fetchAdminBusinessJson`). */
export const adminBusinessQueryKey = ["/api/admin/business"] as const;

/**
 * Loads the booking business profile. Returns `null` when the API responds 404 (no business registered yet);
 * use POST `/api/admin/business` to register, then PATCH for updates.
 */
export async function fetchAdminBusinessJson(): Promise<Business | null> {
  const res = await adminBookingGetAllow404("/api/admin/business");
  if (!res) return null;
  return res.json() as Promise<Business>;
}

/** Same-origin-style `/api/...` paths must be passed through `resolveBookingApiUrl` (see `API_V1_HOSTNAME_KEY`). */
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const resolved = resolveBookingApiUrl(url);

  if (bookingAdminMutationFn && isAdminBookingUrl(resolved)) {
    const result = await bookingAdminMutationFn(method, resolved, data);
    if (result instanceof Response) {
      return result;
    }
    const body = stringifyMutationResult(result);
    return new Response(body, {
      status: 200,
      headers: { "Content-Type": "application/json" },
    });
  }

  const res = await fetch(resolved, {
    method,
    headers: headersForBookingUrl(resolved, !!data),
    body: data ? JSON.stringify(data) : undefined,
    credentials: isPublicBookingApiPath(resolved) ? publicFetchCredentials() : "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
type NotFoundBehavior = "returnNull" | "throw";

export function getQueryFn<T>(options: {
  on401: UnauthorizedBehavior;
  on404?: NotFoundBehavior;
}): QueryFunction<T> {
  const { on401: unauthorizedBehavior, on404 = "throw" } = options;
  return async ({ queryKey }) => {
    const url = resolveBookingApiUrl(queryKey.join("/") as string);

    if (bookingAdminJsonFetch && isAdminBookingUrl(url)) {
      try {
        return (await bookingAdminJsonFetch(url)) as T;
      } catch (e) {
        const err = e as { code?: number };
        if (unauthorizedBehavior === "returnNull" && err?.code === 401) {
          return null as T;
        }
        throw e;
      }
    }

    const res = await fetch(url, {
      credentials: isPublicBookingApiPath(url) ? publicFetchCredentials() : "include",
      headers: headersForBookingUrl(url, false),
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null as T;
    }

    if (on404 === "returnNull" && res.status === 404) {
      return null as T;
    }

    await throwIfResNotOk(res);
    return res.json();
  };
}

/**
 * Admin offerings list uses a paged `useQuery`; full catalogs use `useInfiniteQuery` where needed.
 * `invalidateQueries` alone can leave merged `pages` stale after POST/PATCH/DELETE (e.g. placeholder id `0`
 * still appears after creating a real row). `resetQueries` clears the cache and refetches from page 1.
 */
export async function resetAdminServicesQueries(qc: QueryClient): Promise<void> {
  await qc.resetQueries({ queryKey: ["/api/admin/services"] });
}

export async function resetAdminStaffQueries(qc: QueryClient): Promise<void> {
  await qc.resetQueries({ queryKey: ["/api/admin/staff"] });
}

export async function resetAdminBookingsQueries(qc: QueryClient): Promise<void> {
  await qc.resetQueries({ queryKey: ["/api/admin/bookings"] });
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 2 * 60 * 1000,
    },
    mutations: {
      retry: false,
    },
  },
});
